import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config';
import type { VolunteerHour, CreateVolunteerHourParams, VolunteerStats } from '@/types/volunteer';

/**
 * Add volunteer hours
 */
export async function addVolunteerHours(params: CreateVolunteerHourParams): Promise<string> {
  try {
    // Get volunteer data
    const volunteerDoc = await getDoc(doc(db, 'users', params.volunteerId));
    if (!volunteerDoc.exists()) {
      throw new Error('Volunteer not found');
    }

    const volunteerData = volunteerDoc.data();

    // Get admin data
    const adminDoc = await getDoc(doc(db, 'users', params.addedBy));
    const adminData = adminDoc.exists() ? adminDoc.data() : null;

    // Get organization data if provided
    let organizationName = undefined;
    if (params.organizationId) {
      const orgDoc = await getDoc(doc(db, 'organizations', params.organizationId));
      if (orgDoc.exists()) {
        organizationName = orgDoc.data().name;
      }
    }

    const volunteerHourData: any = {
      volunteerId: params.volunteerId,
      volunteerName: `${volunteerData.firstNameEn} ${volunteerData.lastNameEn}`,
      volunteerNameAr: `${volunteerData.firstNameAr} ${volunteerData.lastNameAr}`,
      volunteerNameEn: `${volunteerData.firstNameEn} ${volunteerData.lastNameEn}`,
      hours: params.hours,
      date: Timestamp.fromDate(params.date),
      activity: params.activity,
      activityAr: params.activityAr,
      activityEn: params.activityEn,
      description: params.description || '',
      descriptionAr: params.descriptionAr || '',
      descriptionEn: params.descriptionEn || '',
      addedBy: params.addedBy,
      addedByName: adminData
        ? `${adminData.firstNameEn} ${adminData.lastNameEn}`
        : 'System',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Only add organizationId and organizationName if they exist
    if (params.organizationId) {
      volunteerHourData.organizationId = params.organizationId;
      if (organizationName) {
        volunteerHourData.organizationName = organizationName;
      }
    }

    const docRef = await addDoc(collection(db, 'volunteerHours'), volunteerHourData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding volunteer hours:', error);
    throw error;
  }
}

/**
 * Update volunteer hours
 */
export async function updateVolunteerHours(
  hourId: string,
  updates: Partial<CreateVolunteerHourParams>
): Promise<void> {
  try {
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };

    if (updates.hours !== undefined) updateData.hours = updates.hours;
    if (updates.date) updateData.date = Timestamp.fromDate(updates.date);
    if (updates.activity) {
      updateData.activity = updates.activity;
      updateData.activityEn = updates.activityEn;
      updateData.activityAr = updates.activityAr;
    }
    if (updates.description !== undefined) {
      updateData.description = updates.description;
      updateData.descriptionEn = updates.descriptionEn;
      updateData.descriptionAr = updates.descriptionAr;
    }

    await updateDoc(doc(db, 'volunteerHours', hourId), updateData);
  } catch (error) {
    console.error('Error updating volunteer hours:', error);
    throw error;
  }
}

/**
 * Delete volunteer hours
 */
export async function deleteVolunteerHours(hourId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, 'volunteerHours', hourId));
  } catch (error) {
    console.error('Error deleting volunteer hours:', error);
    throw error;
  }
}

/**
 * Get volunteer hours by volunteer ID
 */
export async function getVolunteerHoursByVolunteerId(volunteerId: string): Promise<VolunteerHour[]> {
  try {
    const q = query(
      collection(db, 'volunteerHours'),
      where('volunteerId', '==', volunteerId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as VolunteerHour[];
  } catch (error) {
    console.error('Error getting volunteer hours:', error);
    throw error;
  }
}

/**
 * Get all volunteer hours (for admin)
 */
export async function getAllVolunteerHours(): Promise<VolunteerHour[]> {
  try {
    const q = query(collection(db, 'volunteerHours'), orderBy('date', 'desc'));

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as VolunteerHour[];
  } catch (error) {
    console.error('Error getting all volunteer hours:', error);
    throw error;
  }
}

/**
 * Get volunteer statistics
 */
export async function getVolunteerStats(volunteerId: string): Promise<VolunteerStats> {
  try {
    const hours = await getVolunteerHoursByVolunteerId(volunteerId);

    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const firstDayOfYear = new Date(now.getFullYear(), 0, 1);

    const totalHours = hours.reduce((sum, hour) => sum + hour.hours, 0);
    const monthlyHours = hours
      .filter((hour) => hour.date >= firstDayOfMonth)
      .reduce((sum, hour) => sum + hour.hours, 0);
    const yearlyHours = hours
      .filter((hour) => hour.date >= firstDayOfYear)
      .reduce((sum, hour) => sum + hour.hours, 0);

    const lastActivity = hours.length > 0 ? hours[0].date : undefined;

    return {
      totalHours,
      monthlyHours,
      yearlyHours,
      activitiesCount: hours.length,
      lastActivity,
    };
  } catch (error) {
    console.error('Error getting volunteer stats:', error);
    throw error;
  }
}

/**
 * Get volunteer hours by organization
 */
export async function getVolunteerHoursByOrganization(
  organizationId: string
): Promise<VolunteerHour[]> {
  try {
    const q = query(
      collection(db, 'volunteerHours'),
      where('organizationId', '==', organizationId),
      orderBy('date', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      updatedAt: doc.data().updatedAt?.toDate() || new Date(),
    })) as VolunteerHour[];
  } catch (error) {
    console.error('Error getting volunteer hours by organization:', error);
    throw error;
  }
}

